<template>
  <div>
    <Header parent="integrations">
      <template #title>
        {{ integration ? integration.name : "..." }}
      </template>

      <template #actions>
        <BaseButton
          @click="editing = true"
          variant="secondary"
          size="md"
          color="orange-4"
          icon="pencil"
          v-if="canEdit && !editing"
        >
          {{ $t("integrations_market.editor.edit") }}
        </BaseButton>

        <BaseButton
          @click="savePageChanges"
          variant="secondary"
          size="md"
          color="green-5"
          icon="check"
          v-if="canEdit && editing"
        >
          {{ $t("integrations_market.editor.save") }}
        </BaseButton>
      </template>
    </Header>

    <div class="integration-wrapper" v-if="integration">
      <div class="integration-cover">
        <img
          :src="
            integration.cover_image ||
            (editing ? '/icons/integration-cover-placeholder.png' : null)
          "
        />
        <div class="cover-gradient"></div>
        <IntegrationImageEditor
          v-if="editing"
          isCover
          v-model="integration.cover_image"
        />
      </div>

      <div class="integration-contents gap-row-xl">
        <div class="integration-header gap-column-xl">
          <div class="integration-logo">
            <img
              :src="
                integration.logo_image ||
                '/icons/integration-logo-placeholder.png'
              "
            />
            <IntegrationImageEditor
              v-if="editing"
              v-model="integration.logo_image"
            />
          </div>

          <div class="integration-summary w-100 gap-row-xs" v-if="!editing">
            <div class="qrw-heading-xl v1-grey-10-color">
              {{ integrationName }}
            </div>
            <div class="qrw-heading-xs v1-grey-10-color">
              {{ integrationShortDescription }}
            </div>
          </div>
          <div class="integration-summary w-100 gap-row-xs" v-else>
            <div class="qrw-heading-xl v1-grey-10-color">
              <BaseInput
                fullWidth
                v-model="integration.name"
                :placeholder="$t('integrations_market.editor.name_ph')"
              />
            </div>
            <div class="qrw-heading-xs v1-grey-10-color">
              <BaseTextarea
                v-model="integration.short_description"
                :placeholder="$t('integrations_market.editor.short_ph')"
                :rows="3"
              />
            </div>
          </div>
          <div class="integration-actions gap-row-xs">
            <!-- <div
              class="qrw-heading-xs v1-grey-10-color w-100"
              v-if="!editing"
            >
              {{ integration.pricing }}
            </div>
            <div class="qrw-heading-xs v1-grey-10-color w-100" v-else>
              <BaseInput v-model="integration.pricing" fullWidth />
            </div>
            <BaseDivider color="v1-grey-10" class="w-100" /> -->
            <template v-if="!editing">
              <div
                class="connection-actions w-100 gap-column-xs"
                v-if="!isConnected"
              >
                <BaseButton
                  variant="primary"
                  class="w-100"
                  @click="connect"
                  icon="plus"
                  color="v1-grey-10"
                  size="lg"
                >
                  {{ $t("integrations_market.page.connect") }}
                </BaseButton>
              </div>
              <div class="connection-actions w-100 gap-column-xs" v-else>
                <BaseButton
                  variant="primary"
                  class="w-100"
                  @click="connect"
                  disabled
                  color="green-5"
                  icon="check"
                  size="lg"
                >
                  {{ $t("integrations_market.page.connected") }}
                </BaseButton>
                <BaseButton
                  variant="secondary"
                  size="lg"
                  icon="x"
                  @click="disconnect"
                  color="red-4"
                />
              </div>
            </template>
            <div class="v1-grey-10-color" v-else>
              {{ $t("integrations_market.editor.price_hint") }}
            </div>
          </div>
        </div>
        <div
          class="integration-text-block gap-row-sm"
          v-if="integrationDescription || editing"
        >
          <h4 class="qrw-heading-xs w-100 bold v1-grey-6-color">
            {{ $t("integrations_market.page.about") }}
          </h4>

          <p class="qrw-paragraph-md w-100" v-if="!editing">
            {{ integrationDescription }}
          </p>
          <p class="qrw-paragraph-md w-100" v-else>
            <BaseTextarea
              v-model="integration.description"
              class="w-100"
              :rows="6"
            />
          </p>
        </div>

        <div
          class="integration-screenshots-block gap-row-sm"
          v-if="(integration.media && integration.media.length) || editing"
        >
          <h4
            class="qrw-heading-xs bold v1-grey-6-color pl-40 pr-40 w-100"
          >
            {{ $t("integrations_market.page.screenshots") }}
          </h4>

          <IQOSHorizontalScroll :height="390" class="screenshots-container">
            <div
              class="media-container"
              v-for="(m, mi) in integration.media"
              :key="mi"
            >
              <img
                :src="
                  m.url ||
                  (editing ? '/icons/integration-media-placeholder.png' : null)
                "
                alt=""
              />
              <IntegrationImageEditor
                v-model="m.url"
                isCover
                @delete="integration.media.splice(mi, 1)"
                v-if="editing"
              />
            </div>

            <template v-if="editing">
              <div
                class="placeholder"
                v-for="n in Math.max(8 - integration.media.length, 0)"
                :key="n"
              >
                <img src="/icons/integration-media-placeholder.png" alt="" />
                <IntegrationImageEditor
                  :value="null"
                  isCover
                  @input="onMediaUploaded"
                />
              </div>
            </template>
          </IQOSHorizontalScroll>
        </div>

        <div
          class="integration-text-block gap-row-sm"
          v-if="integration.contacts"
        >
          <h4 class="qrw-heading-xs bold v1-grey-6-color">
            {{ $t("integrations_market.page.contacts") }}
          </h4>
          <div class="integration-contacts-list gap-row-xs">
            <div
              class="contact-item"
              v-if="integration.contacts.phone || editing"
            >
              <BaseIcon icon="phone-bold" />
              <a
                :href="`tel:${integration.contacts.phone}`"
                class="qrw-paragraph-md v1-grey-10-color"
                v-if="!editing"
              >
                {{ integration.contacts.phone }}
              </a>
              <BaseInput v-model="integration.contacts.phone" v-else />
            </div>
            <div
              class="contact-item"
              v-if="integration.contacts.email || editing"
            >
              <BaseIcon icon="mail" />
              <a
                :href="`mailto:${integration.contacts.email}`"
                class="qrw-paragraph-md v1-grey-10-color"
                v-if="!editing"
              >
                {{ integration.contacts.email }}
              </a>
              <BaseInput v-model="integration.contacts.email" v-else />
            </div>
            <div class="contact-item" v-if="integration.contacts.website">
              <BaseIcon icon="globe" />
              <a
                :href="integration.contacts.website"
                target="_blank"
                class="qrw-paragraph-md v1-grey-10-color"
                v-if="!editing"
              >
                {{ integration.contacts.website }}
              </a>
              <BaseInput v-model="integration.contacts.website" v-else />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.integration-wrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 40px;
}

.integration-cover {
  width: 100%;
  height: 397px;
  position: relative;
}

.integration-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-gradient {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;

  background: linear-gradient(180deg, rgba(252, 252, 252, 0) 0%, #fcfcfc 100%);
}

.integration-header {
  padding: 40px 40px 0 40px;

  align-content: center;
  align-items: center;
}

.integration-logo {
  height: 128px;
  width: 256px;
  max-width: 256px;
  max-height: 128px;
  min-width: 256px;
  max-height: 128px;
  border-radius: 8px;
  position: relative;
  clip-path: border-box;
}

.integration-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.integration-actions {
  min-width: 226px;
  width: 226px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.integration-actions div {
  text-align: center;
}

.integration-text-block {
  width: 100%;
  padding: 0 40px;
}

.contact-item {
  display: flex;
  align-items: center;
  align-content: center;

  column-gap: 8px;
}

.contact-item a {
  text-decoration: none;
}

.screenshots-container img {
  width: auto;
  height: 100%;
  border-radius: 16px;
  object-fit: cover;
}

.screenshots-container .placeholder {
  width: 186px;
  height: 100%;

  border-radius: 16px;

  position: relative;
}

.media-container {
  position: relative;
}

.integration-screenshots-block,
.integration-screenshots-block h4,
.integration-text-block {
  box-sizing: border-box;
}
</style>

<script>
import IntegrationsAPI from "@/api/integrations";

import IntegrationImageEditor from "@/components/integrations/IntegrationImageEditor.vue";

export default {
  data() {
    return {
      integration: {},
      editing: false,
    };
  },
  components: {
    IntegrationImageEditor,
  },
  methods: {
    fetchIntegration() {
      IntegrationsAPI.getIntegration(this.$route.params.id)
        .then((data) => {
          this.integration = data.integration;

          if (!this.integration.media) {
            this.integration.media = [];
          }
        })
        .catch((err) => {});
    },
    connect() {
      if (
        this.integration.platform_options &&
        this.integration.platform_options.connect_url
      ) {
        window.location.href = this.integration.platform_options.connect_url;
        return;
      }

      this.$store.dispatch("integrations/connectIntegration", this.integration);
    },
    disconnect() {
      if (
        this.integration.platform_options &&
        this.integration.platform_options.disconnect_url
      ) {
        window.location.href = this.integration.platform_options.disconnect_url;
        return;
      }

      this.$store.dispatch(
        "integrations/disconnectIntegration",
        this.integration
      );
    },
    savePageChanges() {
      this.editing = false;

      IntegrationsAPI.updateIntegrationPage(
        this.integration._id,
        this.integration
      )
        .then((data) => {
          this.$store.commit("ui/addToast", {
            variant: "confirm",
            content: this.$t("notification_toast.confirm.saved"),
          });

          this.integration = data.integration;
        })
        .catch((err) => {
          this.$store.commit("ui/addToast", {
            variant: "error",
            content: "integration",
          });
        });
    },
    onMediaUploaded(url) {
      this.integration.media.push({
        url: url,
        media_type: "image",
      });
    },
  },
  computed: {
    isConnected() {
      return this.$store.getters["integrations/isConnected"](
        this.integration._id
      );
    },
    integrationLocalizedInfo() {
      const defaultInfo = {
        name: this.integration.name,
        description: this.integration.description,
        short_description: this.integration.short_description,
      };

      if (!this.integration.i18n) return defaultInfo;

      const i18n = this.integration.i18n[this.$i18n.locale];

      if (!i18n) return defaultInfo;

      return {
        name: i18n.name || defaultInfo.name,
        description: i18n.description || defaultInfo.description,
        short_description:
          i18n.short_description || defaultInfo.short_description,
      };
    },
    integrationShortDescription() {
      return this.integrationLocalizedInfo.short_description;
    },
    integrationDescription() {
      return this.integrationLocalizedInfo.description;
    },
    integrationName() {
      return this.integrationLocalizedInfo.name;
    },
    canEdit() {
      return this.integration.author_id === this.$store.state.owner._id;
    },
  },
  mounted() {
    this.fetchIntegration();
  },
};
</script>
